import { Paper } from "@material-ui/core"
import InvoiceForm from "components/invoice/InvoiceForm"
import NotAllowedNotice from "components/NotAllowedNotice"
import PERMISSION_CODES from "constants/permission-codes"
import { ROUTE_INVOICE_ROOT } from "constants/routeConstants"
import React, { useMemo } from "react"
import { useHistory, useLocation } from "react-router-dom"
import ApiCustomer from "services/api-customer"
import ApiDeliveryNotes from "services/api-delivery-notes"
import ApiAuthorizedSignature from "services/api-authorized-signature"
import ApiCurrency from "services/api-master-data-currency"
import useCheckPermission from "utils/auth/useCheckPermission"
import {
  sendAndHandleInvalidToken,
  useWrapHandleInvalidToken,
} from "utils/invalid-token"
import useShowSnackbar from "utils/snackbar-hooks"
import ApiInvoice from "services/api-invoice"
import ApiSalesQuotation from "services/api-sales-quotation"

const PAGE_PERMISSIONS = [PERMISSION_CODES.WRITE_INVOICE]

const fetchDeliveryNote = async ({
  searchText = "",
  customerId = "",
  currencyId = "",
}) =>
  (
    (await ApiDeliveryNotes.get({
      searchText,
      customerId,
      currencyId,
      fromColumns: ["dnNumber"],
      limit: 10,
      notPaid: true,
      fixed: true,
    })) || []
  ).data

const fetchCurrency = async () => ((await ApiCurrency.get()) || []).data

const fetchCustomer = async () => ((await ApiCustomer.get()) || []).data

const fetchAuthorizedSignature = async () =>
  ((await ApiAuthorizedSignature.get()) || []).data

const fetchSalesQuotation = async ({
  slsQuotNumber,
  rateInId,
  customerId,
  haveWorkOrder = null,
}) => {
  const params = {
    searchText: slsQuotNumber,
    fromColumns: ["slsQuotNumber"],
    status: "execute",
    limit: 10,
    rateInId,
    customerId,
    paymentStatuses: [1, 2],
  }

  if (haveWorkOrder !== null) {
    params.haveWorkOrder = haveWorkOrder
  }

  const response = await ApiSalesQuotation.getSQForInvoice(params)

  return response.data
}

const fetchPOData = async ({
  poNumber = "",
  rateInId = "",
  customerId = "",
  ...otherParams
}) =>
  (
    (await ApiSalesQuotation.getPOForInvoice({
      poNumber,
      rateInId,
      customerId,
      paymentStatuses: [1, 2],
      ...otherParams,
    })) || []
  ).data

const fetchDeletedInvoiceData = async (searchText) =>
  (
    await ApiInvoice.get({
      searchText,
      fromColumns: ["invoiceNumber"],
      includeCanceled: true,
      status: "cancel",
    })
  ).data

function InvoiceCreatePage() {
  const history = useHistory()
  const [showSnackbar, showSnackbarLoading, hideSnackbar] = useShowSnackbar()

  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const invoiceType = queryParams.get("invoiceType")

  const { checkWithoutUpdateState } = useCheckPermission()

  const wrappedFetchDeliveryNoteData = useWrapHandleInvalidToken(
    fetchDeliveryNote,
    () => []
  )
  const wrappedFetchPOData = useWrapHandleInvalidToken(fetchPOData, () => [])

  const wrappedFetchCurrency = useWrapHandleInvalidToken(
    fetchCurrency,
    () => []
  )

  const wrappedFetchCustomerData = useWrapHandleInvalidToken(
    fetchCustomer,
    () => []
  )

  const wrappedFetchAuthorizedSignature = useWrapHandleInvalidToken(
    fetchAuthorizedSignature,
    () => []
  )

  const wrappedFetchSalesQuotationData = useWrapHandleInvalidToken(
    fetchSalesQuotation,
    () => []
  )

  const allowed = useMemo(
    () => checkWithoutUpdateState({ permissions: PAGE_PERMISSIONS }),
    [checkWithoutUpdateState]
  )

  const handleCancel = () => {
    history.push(ROUTE_INVOICE_ROOT)
  }

  const createSignatureIfNeeded = async (formData) => {
    // jika sudah pilih dari list → sudah ada ID
    if (formData.authorizedSignatureId) return formData
    const typed = (formData.authorizedSignatureText || "").trim()
    if (!typed) throw new Error("Signed By wajib diisi.")

    const res = await sendAndHandleInvalidToken(() =>
      ApiAuthorizedSignature.create({
        name: typed,
        is_active: true,
        position: null,
      })
    )
    console.log({ res })
    if (!res.success)
      throw new Error("Gagal membuat Authorized Signature baru.")
    const created = res.response.data
    const newId = created?.idAuthorizedSignature
    if (!newId)
      throw new Error("ID Authorized Signature tidak ditemukan dari server.")

    // sisipkan ID ke payload invoice; buang teks bantu
    return {
      ...formData,
      authorizedSignatureId: Number(newId),
      authorizedSignatureText: undefined,
    }
  }

  const handleSubmit = async (formData) => {
    // showSnackbarLoading("Creating invoice data...")
    // let fetchState = {}

    // try {
    //   fetchState = await sendAndHandleInvalidToken(() =>
    //     ApiInvoice.create(formData)
    //   )
    // } catch (err) {
    //   showSnackbar(err?.errors[0]?.message ?? "Cannot create invoice")
    //   return
    // }
    // if (fetchState.success) {
    //   showSnackbar("Invoice data successfully created")
    //   const { data } = fetchState.response
    //   history.push(`${ROUTE_INVOICE_ROOT}/${data.idInvoice}`)
    // } else {
    //   hideSnackbar()
    // }
    try {
      // 1) pastikan ada authorizedSignatureId
      showSnackbarLoading("Creating invoice data...")
      const ready = await createSignatureIfNeeded(formData)
      const { authorizedSignatureText, ...payload } = ready
      // (opsional) normalisasi angka
      if (payload.paymentPercentage != null)
        payload.paymentPercentage = Number(payload.paymentPercentage)
      if (payload.currencyRate != null)
        payload.currencyRate = Number(payload.currencyRate)
      if (payload.idDeliveryNote != null)
        payload.idDeliveryNote = Number(payload.idDeliveryNote)
      // 2) create invoice
      const fetchState = await sendAndHandleInvalidToken(() =>
        ApiInvoice.create(payload)
      )
      if (fetchState.success) {
        showSnackbar("Invoice data successfully created")
        const { data } = fetchState.response
        history.push(`${ROUTE_INVOICE_ROOT}/${data.idInvoice}`)
      } else {
        hideSnackbar()
      }
    } catch (err) {
      showSnackbar(
        err?.message ?? err?.errors?.[0]?.message ?? "Cannot create invoice"
      )
    }
  }

  if (!allowed) {
    return (
      <Paper style={{ maxWidth: "800px" }}>
        <NotAllowedNotice />
      </Paper>
    )
  }

  return (
    <InvoiceForm
      initialValue={{ invoiceType }}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      fetchDeliveryNoteData={wrappedFetchDeliveryNoteData}
      handleSubmit={handleSubmit}
      fetchCurrencyData={wrappedFetchCurrency}
      fetchCustomerData={wrappedFetchCustomerData}
      fetchAuthorizedSignatureData={wrappedFetchAuthorizedSignature}
      fetchSalesQuotationData={wrappedFetchSalesQuotationData}
      fetchPOData={wrappedFetchPOData}
      fetchDeletedInvoiceData={fetchDeletedInvoiceData}
    />
  )
}

export default InvoiceCreatePage
